/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ESIM_DATA_BUCKETS,
  ESIM_VALIDITY_BUCKETS,
} from '../../constants/esimConstants';
import { dataCapacityInGb, isUnlimitedPlan } from '../../utils/esimUtils';
import './EsimPlanFilters.scss';

const matchDataBucket = (plan, bucket) => {
  if (bucket.key === 'unlimited') return isUnlimitedPlan(plan);
  const gb = dataCapacityInGb(plan);
  return gb > bucket.min && gb <= bucket.max;
};

const matchValidityBucket = (plan, bucket) => {
  const v = Number(plan?.validity || 0);
  return v > bucket.min && v <= bucket.max;
};

function EsimPlanFilters({ plans, filters, onChange }) {
  const dataCounts = useMemo(() => {
    const counts = {};
    ESIM_DATA_BUCKETS.forEach((b) => {
      counts[b.key] = plans.filter((p) => matchDataBucket(p, b)).length;
    });
    return counts;
  }, [plans]);

  const validityCounts = useMemo(() => {
    const counts = {};
    ESIM_VALIDITY_BUCKETS.forEach((b) => {
      counts[b.key] = plans.filter((p) => matchValidityBucket(p, b)).length;
    });
    return counts;
  }, [plans]);

  const togglePill = (group, key) => {
    const current = new Set(filters[group] || []);
    if (current.has(key)) current.delete(key);
    else current.add(key);
    onChange({ ...filters, [group]: Array.from(current) });
  };

  const clearAll = () => {
    onChange({ ...filters, dataBuckets: [], validityBuckets: [] });
  };

  return (
    <aside className="esim-filters">
      <header className="esim-filters__head">
        <h3>Filters</h3>
        <button
          type="button"
          className="esim-filters__clear"
          onClick={clearAll}
        >
          Clear
        </button>
      </header>

      <section className="esim-filters__group">
        <h4>Data Allowance</h4>
        <div className="esim-filters__pills">
          {ESIM_DATA_BUCKETS.map((b) => {
            const active = (filters.dataBuckets || []).includes(b.key);
            const count = dataCounts[b.key] || 0;
            return (
              <button
                key={b.key}
                type="button"
                className={`esim-filters__pill ${
                  active ? 'esim-filters__pill--active' : ''
                } ${count === 0 ? 'esim-filters__pill--disabled' : ''}`}
                disabled={count === 0}
                onClick={() => togglePill('dataBuckets', b.key)}
              >
                {b.label}
                <span>{count}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="esim-filters__group">
        <h4>Validity</h4>
        <div className="esim-filters__pills">
          {ESIM_VALIDITY_BUCKETS.map((b) => {
            const active = (filters.validityBuckets || []).includes(b.key);
            const count = validityCounts[b.key] || 0;
            return (
              <button
                key={b.key}
                type="button"
                className={`esim-filters__pill ${
                  active ? 'esim-filters__pill--active' : ''
                } ${count === 0 ? 'esim-filters__pill--disabled' : ''}`}
                disabled={count === 0}
                onClick={() => togglePill('validityBuckets', b.key)}
              >
                {b.label}
                <span>{count}</span>
              </button>
            );
          })}
        </div>
      </section>
    </aside>
  );
}

EsimPlanFilters.propTypes = {
  plans: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default EsimPlanFilters;
